import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';

/** Dealer "typing" pause before a bubble lands. */
const TYPING_MS = 750;
/** Beat between consecutive entries, so the thread reads at conversation pace. */
const BETWEEN_BUBBLE_MS = 350;

/** Exported for the one wait the flow paces itself — the pre-navigation checkout handoff. */
export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Everything the thread appends that isn't a bubble or a typing indicator. */
type ChatCard =
  | { kind: 'attachment' }
  /** `batch` marks which delivery a card came from — only the newest set is still live,
   *  since earlier ones are conversation history the customer has already answered. */
  | { kind: 'product-card'; quoteItemIds: string[]; batch: number }
  | { kind: 'customer-info-card' }
  /** Bound to one cart entry — payment is chosen per product, so the thread carries
   *  one of these per plan. */
  | { kind: 'payment-card'; cartIndex: number }
  /** The review: every plan with its own terms, then due today vs monthly — the
   *  marketplace order summary's counterpart. */
  | { kind: 'summary-card' };

type ChatMessage =
  | { id: string; kind: 'bubble'; side: 'dealer' | 'customer'; text: string }
  | { id: string; kind: 'typing' }
  | (ChatCard & { id: string });

interface Transcript {
  messages: ChatMessage[];
  /** Attach to the scrolling thread container; the hook keeps it pinned to the bottom. */
  scrollRef: RefObject<HTMLDivElement>;
  /** Beat, typing indicator, then the dealer bubble replaces it in place. */
  say: (text: string) => Promise<void>;
  /** Dealer bubble with no pause — for when a request is already in flight. */
  sayNow: (text: string) => void;
  /** Customer-side bubble echoing what they just tapped or typed. */
  echo: (text: string) => void;
  /** Beat, then append a card. */
  show: (card: ChatCard) => Promise<void>;
  /**
   * Marks a wait in the thread — a bubble, or a typing indicator when `text` is
   * omitted — and returns the function that clears it once the wait resolves.
   */
  pending: (text?: string) => () => void;
}

/**
 * Owns the chat transcript and its pacing. Every write is dropped once the session
 * unmounts, so a sequence still in flight when the customer closes the chat can't
 * settle onto a dead component.
 */
export function useTranscript(): Transcript {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const idCounter = useRef(0);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, [messages]);

  // Built once and never rebuilt: these close over refs and `setMessages` only, so a
  // stable identity lets the delivery effect depend on them without restarting itself
  // on every appended message.
  const writers = useMemo(() => {
    // Ids are minted outside the updater — a state updater must stay pure.
    function nextId(): string {
      idCounter.current += 1;
      return `m-${idCounter.current}`;
    }

    function write(next: (prev: ChatMessage[]) => ChatMessage[]): void {
      if (!mounted.current) return;
      setMessages(next);
    }

    function append(message: ChatMessage): void {
      write((prev) => [...prev, message]);
    }

    function sayNow(text: string): void {
      append({ id: nextId(), kind: 'bubble', side: 'dealer', text });
    }

    function echo(text: string): void {
      append({ id: nextId(), kind: 'bubble', side: 'customer', text });
    }

    async function say(text: string): Promise<void> {
      await sleep(BETWEEN_BUBBLE_MS);
      if (!mounted.current) return;
      // The bubble replaces the indicator in place rather than swapping two messages,
      // so the entry animates once instead of flickering.
      const id = nextId();
      append({ id, kind: 'typing' });
      await sleep(TYPING_MS);
      write((prev) =>
        prev.map((m) => (m.id === id ? { id, kind: 'bubble', side: 'dealer', text } : m)),
      );
    }

    async function show(card: ChatCard): Promise<void> {
      await sleep(BETWEEN_BUBBLE_MS);
      append({ ...card, id: nextId() });
    }

    function pending(text?: string): () => void {
      const id = nextId();
      append(
        text === undefined ? { id, kind: 'typing' } : { id, kind: 'bubble', side: 'dealer', text },
      );
      return () => write((prev) => prev.filter((m) => m.id !== id));
    }

    return { say, sayNow, echo, show, pending };
  }, []);

  return { messages, scrollRef, ...writers };
}
